import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import Nav from "react-bootstrap/Nav";
import { Navbar, Dropdown } from "react-bootstrap";
import { Link, useLocation } from 'react-router-dom';
import Logo from '../common/Logo';
import TenantSelector from '../common/TenantSelector';
import ImpersonationBanner from '../common/ImpersonationBanner';
import TokenDisplay from '../common/TokenDisplay';

const HeaderLoggedInDesktop = ({auth}) => {

    const [showDropdown, setShowDropdown] = useState(false);
    const [tenantRole, setTenantRole] = useState(null);
    const [tenantCount, setTenantCount] = useState(0);
    const dropdownRef = useRef(null);
    const location = useLocation();

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
        ) {
            setShowDropdown(false);
        }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Fetch tenant role and count for current user
    useEffect(() => {
        const fetchTenantInfo = async () => {
            if (!auth?.id) return;
            try {
                const response = await fetch('/api/user/tenants', { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    const activeTenant = data.tenants?.find(t => t.id === data.activeTenantId);
                    setTenantRole(activeTenant?.role || null);
                    setTenantCount(data.tenants?.length || 0);
                }
            } catch (err) {
                console.error('Error fetching tenant info:', err);
            }
        };
        fetchTenantInfo();
    }, [auth?.id]);

    // Add null checks to prevent errors when auth data is still loading
    const userInitial = auth?.first_name ? auth.first_name.charAt(0).toUpperCase() : null;

    const navbarStyle = {
        height: "56px",
        background: 'rgba(255, 255, 255, 0.98)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)',
        paddingRight: "5px"
    };

    // Don't render if auth data isn't loaded yet
    if (!auth || !auth.id) {
        return (
            <div className="d-lg-block d-none">
                <Navbar fixed="top" collapseOnSelect expand="lg" className="navbar-custom" style={navbarStyle}>
                    <Navbar.Brand>
                        <Link to="/" style={{color: "#1a1a2e", textDecoration:"none", marginLeft:"10px", display: 'flex', alignItems: 'center'}}>
                            <Logo size={28} color="#6366f1" showText textColor="#1a1a2e" />
                        </Link>
                    </Navbar.Brand>
                </Navbar>
            </div>
        );
    }

    const isAdmin = auth.platform_role === 'super_admin' || auth.platform_role === 'admin';
    const isTenantAdmin = tenantRole === 'admin' || tenantRole === 'manager';
    const hasMultipleTenants = tenantCount > 1;

    return (
        <>
        <ImpersonationBanner />
        <div className="d-lg-block d-none">
                <Navbar fixed="top" collapseOnSelect expand="lg" className="navbar-custom" style={navbarStyle}>

                        <Navbar.Brand>
                            <Link to="/dashboard" style={{color: "#1a1a2e", textDecoration:"none", marginLeft:"10px", display: 'flex', alignItems: 'center'}}>
                                <Logo size={28} color="#6366f1" showText textColor="#1a1a2e" />
                            </Link>
                        </Navbar.Brand>

                        <Nav className="ms-auto d-flex align-items-center">
                            {/* Dashboard */}
                            <Link to="/dashboard"
                                className="d-inline-flex align-items-center"
                                style={{
                                    color: "#4a5568",
                                    textDecoration: "none",
                                    fontSize: "0.9rem",
                                    fontWeight: "500",
                                    marginRight: "1rem"
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.3rem' }}>
                                    <rect x="3" y="3" width="7" height="7"/>
                                    <rect x="14" y="3" width="7" height="7"/>
                                    <rect x="14" y="14" width="7" height="7"/>
                                    <rect x="3" y="14" width="7" height="7"/>
                                </svg>
                                Dashboard
                            </Link>

                            {/* Token Display */}
                            <TokenDisplay />

                            {/* Tenant Selector - only show if user has multiple tenants */}
                            {hasMultipleTenants && (
                                <>
                                    <span style={{ color: '#e2e8f0', margin: '0 0.75rem', fontSize: '1.2rem' }}>|</span>
                                    <TenantSelector />
                                </>
                            )}

                            {/* User Avatar Dropdown */}
                            <div className="d-flex align-items-center" style={{ marginLeft: '0.75rem' }} ref={dropdownRef}>
                                <Dropdown show={showDropdown} onToggle={() => {}}>
                                    <div
                                    as="div"
                                    onClick={() => setShowDropdown(!showDropdown)}
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '50%',
                                        backgroundColor: '#6366f1',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        fontSize:"0.9em",
                                    }}
                                    >
                                        {userInitial === null ? <i className="bi bi-person-fill"></i> : userInitial}
                                    </div>

                                    <Dropdown.Menu align="end" show={showDropdown} className="custom-dropdown-menu p-0" style={{ minWidth: '180px' }}>
                                        <Dropdown.Item
                                            as={Link}
                                            to="/dashboard/settings"
                                            className="py-2"
                                            onClick={() => setShowDropdown(false)}
                                            style={{ fontSize:"0.9rem", textDecoration: 'none' }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                <circle cx="12" cy="7" r="4"/>
                                            </svg>
                                            Profile Settings
                                        </Dropdown.Item>

                                        {/* Org Admin - only for tenant admins/managers */}
                                        {isTenantAdmin && (
                                            <Dropdown.Item
                                                as={Link}
                                                to="/dashboard/settings#organization-settings"
                                                className="py-2"
                                                onClick={() => setShowDropdown(false)}
                                                style={{ fontSize:"0.9rem", textDecoration: 'none' }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="9" cy="7" r="4"/>
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                </svg>
                                                Org Admin
                                            </Dropdown.Item>
                                        )}

                                        {/* Platform - for super admins/admins */}
                                        {isAdmin && (
                                            <Dropdown.Item
                                                as={Link}
                                                to="/admin/tenants"
                                                className="py-2"
                                                onClick={() => setShowDropdown(false)}
                                                style={{ fontSize:"0.9rem", textDecoration: 'none', color: '#6366f1' }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                                </svg>
                                                Platform Admin
                                            </Dropdown.Item>
                                        )}

                                        <Dropdown.Divider style={{ margin: '0' }} />
                                        <Dropdown.Item
                                            as={Link}
                                            to="/logout"
                                            className="py-2"
                                            onClick={() => setShowDropdown(false)}
                                            style={{ color:"#dc3545", fontSize:"0.9rem", textDecoration: 'none' }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                                <polyline points="16 17 21 12 16 7"/>
                                                <line x1="21" y1="12" x2="9" y2="12"/>
                                            </svg>
                                            Logout
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                        </Nav>

                </Navbar>
        </div>
        </>
    )
}

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(HeaderLoggedInDesktop);
